import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Upload, Plus, CheckCircle2, XCircle, Trash, Cloud, RefreshCw, AlertTriangle, FolderPlus, RotateCcw, FileText, FileSpreadsheet, Loader2 } from 'lucide-react';

interface Template { id: string; name: string; current_published_version?: number; }
interface Job { id: string; name: string; template_id: string; template_version: number; status: string; total_rows: number; approved_rows: number; rejected_rows: number; reviewed_rows: number; created_at: string }
interface Row {
  id: string; job_id: string; source_key: string; site_reference: string;
  review_status: string; ocr_status: string; extracted_answers: any[];
  notes: string; approved_result_id?: string;
  replaces?: string[];
  superseded_by?: string | null;
  original_filename?: string;
}
interface Version { id: string; version: number; status: string; schema: any }
interface FullTemplate { versions: Version[] }

// Small stat pill used by the OneDrive scan panel.
const Stat: React.FC<{ label: string; value: number | string; tone: 'success' | 'blue' | 'destructive' | 'muted' }>
  = ({ label, value, tone }) => {
  const bg = tone === 'success' ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
            : tone === 'blue'   ? 'bg-raptor-blue/10 border-raptor-blue/40 text-raptor-navy'
            : tone === 'destructive' ? 'bg-red-50 border-red-300 text-red-900'
            : 'bg-raptor-neutral/40 border-border/60 text-raptor-navy';
  return (
    <div className={`rounded-md border p-2 ${bg}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-lg font-headline">{value}</div>
    </div>
  );
};

export const ImportsPage: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [activeJob, setActiveJob] = useState<Job | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [creating, setCreating] = useState(false);
  const [showChooser, setShowChooser] = useState(false);
  const nav = useNavigate();
  const [form, setForm] = useState({ name: '', template_id: '', notes: '' });
  const fileRef = useRef<HTMLInputElement>(null);
  const [reviewing, setReviewing] = useState<Row | null>(null);
  const [reviewSchema, setReviewSchema] = useState<any>(null);
  const [reviewAnswers, setReviewAnswers] = useState<Record<string, string>>({});
  const [reviewMeta, setReviewMeta] = useState({ surveyor_name: '', client_name: '', site_reference: '' });
  // True when the currently-open review modal is a read-only preview of
  // an already-approved / rejected row. Hides the Approve / Reject
  // buttons and dims the form inputs.
  const [reviewReadOnly, setReviewReadOnly] = useState<boolean>(false);
  const [reviewPhotos, setReviewPhotos] = useState<Array<{ id: string; storage_key?: string; width?: number; height?: number; page?: number }>>([]);
  // Photo blob URLs are keyed by photo id. We fetch bytes over the
  // authenticated axios client (the /thumb endpoint requires a bearer
  // token, which <img> tags can't send) and hand a fresh
  // ``URL.createObjectURL`` string to each <img>.  Cleanup happens in
  // the effect below to avoid leaking blob URLs across modal opens.
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  // Excel bulk-import jobs (Commit B). Kept as a separate list so the
  // panel can refresh without perturbing the PDF-import jobs table.
  interface ExcelJob {
    id: string; filename: string; sheet: string; row_count: number;
    columns: unknown[]; created_at: string; status?: string;
    client_id?: string | null; client_name_cache?: string | null;
    template_id?: string | null; template_name_cache?: string | null;
    template_version?: number | null;
    commit_summary?: { sites_created: number; results_created: number } | null;
  }
  const [excelJobs, setExcelJobs] = useState<ExcelJob[]>([]);
  const [rollingBackId, setRollingBackId] = useState<string | null>(null);
  const [scanId, setScanId] = useState<string | null>(null);
  const [scanState, setScanState] = useState<Record<string, unknown> | null>(null);
  const [scanEntries, setScanEntries] = useState<Array<Record<string, unknown>>>([]);
  const [scanFilter, setScanFilter] = useState<'' | 'ingested' | 'duplicate' | 'failed'>('');
  const scanTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Completion dialog: shown once when a scan transitions to a terminal
  // state (succeeded/failed). Keeps the user in the loop and provides a
  // one-click path to the freshly-imported job so they can start the
  // review workflow immediately.
  const [scanCompletion, setScanCompletion] = useState<null | {
    status: 'succeeded' | 'failed';
    ingested: number;
    duplicate: number;
    failed: number;
    discovered: number;
    error?: string;
  }>(null);
  const lastScanStatusRef = useRef<string | null>(null);

  const load = async () => {
    const [t, j, ex] = await Promise.all([
      api.get<Template[]>('/templates'),
      api.get<Job[]>('/imports/jobs'),
      api.get<ExcelJob[]>('/imports/excel'),
    ]);
    setTemplates(t.data); setJobs(j.data); setExcelJobs(ex.data || []);
    if (!form.template_id && t.data.length) setForm(f => ({ ...f, template_id: t.data[0].id }));
    // Rehydrate any in-flight OneDrive scan so the button reflects the
    // correct state after a page refresh (prevents double-scan races).
    // Guard: only rehydrate if the scan is younger than 10 minutes — an
    // older "running" record is almost certainly a zombie left behind by
    // a backend restart. The backend also sweeps these at startup, but
    // this belt-and-braces check protects the UI in edge cases.
    try {
      const scans = await api.get<Array<Record<string, unknown>>>('/imports/scans?limit=5');
      const active = (scans.data ?? []).find(
        (s) => s.status === 'queued' || s.status === 'running',
      );
      const createdAt = active?.created_at ? new Date(String(active.created_at)).getTime() : 0;
      const ageMs = Date.now() - createdAt;
      const isFresh = createdAt > 0 && ageMs < 10 * 60 * 1000; // 10 minutes
      if (active && typeof active.id === 'string' && isFresh) {
        setScanId(active.id);
        setScanState(active);
      } else if (active) {
        // Stale — silently ignore so the user isn't stuck in "Scanning…".
        // eslint-disable-next-line no-console
        console.info('[imports] Ignoring stale scan', active.id, 'age', Math.round(ageMs / 1000), 's');
      }
    } catch { /* silent — non-critical */ }
  };
  useEffect(() => { load(); }, []);

  const openJob = async (jid: string) => {
    const [j, r] = await Promise.all([api.get<Job>(`/imports/jobs/${jid}`), api.get<Row[]>(`/imports/jobs/${jid}/rows`)]);
    setActiveJob(j.data); setRows(r.data);
  };

  const createJob = async (e: React.FormEvent) => {
    e.preventDefault();
    try { const r = await api.post('/imports/jobs', form); toast.success('Job created'); setCreating(false); await load(); openJob(r.data.id); }
    catch (err: unknown) { toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed'); }
  };

  const uploadPdfs = async (files: FileList | null) => {
    if (!files || !activeJob) return;
    let uploaded = 0;
    let replaced = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const fd = new FormData();
      fd.append('file', f);
      try {
        const r = await api.post<{ replaced_count?: number }>(
          `/imports/jobs/${activeJob.id}/upload`, fd,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        uploaded += 1;
        if (r.data?.replaced_count) replaced += r.data.replaced_count;
      } catch {
        toast.error(`Failed to upload "${f.name}".`);
      }
    }
    const parts: string[] = [`${uploaded} uploaded`];
    if (replaced) parts.push(`${replaced} older version${replaced === 1 ? '' : 's'} replaced`);
    toast.success(parts.join(' · '));
    if (fileRef.current) fileRef.current.value = '';
    openJob(activeJob.id);
  };

  const openReview = async (row: Row) => {
    if (!activeJob) return;
    // Guard against jobs with no template (e.g. legacy auto-managed jobs
    // that pre-date default-template assignment). Instead of a 404, show
    // a helpful toast pointing the operator at the fix.
    if (!activeJob.template_id) {
      toast.error(
        'This import job has no template attached, so Review cannot render the form. Re-scan OneDrive to auto-assign the current Deposita template.',
      );
      return;
    }
    setReviewing(row);
    setReviewPhotos([]);
    const readOnly = row.review_status === 'approved' || row.review_status === 'rejected';
    setReviewReadOnly(readOnly);
    const meta = (row as any).extracted_meta || {};
    setReviewMeta({
      surveyor_name: meta.surveyor_name || '',
      client_name: meta.client_name || '',
      site_reference: row.site_reference || meta.site_reference || '',
    });
    try {
      const t = await api.get<FullTemplate>(`/templates/${activeJob.template_id}`);
      const v = t.data.versions.find((x: any) => x.version === activeJob.template_version);
      if (!v) {
        toast.error(`Template version v${activeJob.template_version} not found on ${activeJob.template_id}.`);
        setReviewing(null);
        return;
      }
      setReviewSchema(v.schema);

      // For an approved row, load the submitted survey_result so the
      // reviewer sees exactly what was recorded — not the pre-extraction
      // heuristics. Falls back to extracted_answers on error.
      let answers: any[] = row.extracted_answers || [];
      if (readOnly && row.review_status === 'approved') {
        try {
          const r = await api.get<{
            result: { answers: any[]; surveyor_name?: string; client_name?: string };
            instruction: { site_reference?: string } | null;
            photos: Array<{ id: string; storage_key?: string; width?: number; height?: number; page?: number }>;
          }>(`/imports/rows/${row.id}/result`);
          answers = r.data.result?.answers ?? answers;
          setReviewPhotos(r.data.photos ?? []);
          if (r.data.result?.surveyor_name || r.data.result?.client_name) {
            setReviewMeta((prev) => ({
              surveyor_name: r.data.result.surveyor_name ?? prev.surveyor_name,
              client_name: r.data.result.client_name ?? prev.client_name,
              site_reference: r.data.instruction?.site_reference ?? prev.site_reference,
            }));
          }
        } catch { /* silent — keep extracted_answers fallback */ }
      }

      const map: Record<string, string> = {};
      answers.forEach((a: any) => { map[a.question_code] = String(a.value ?? ''); });
      setReviewAnswers(map);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      toast.error(
        status === 404
          ? `Template ${activeJob.template_id} not found. It may have been deleted after this job was created.`
          : 'Failed to load the template for this row.',
      );
      setReviewing(null);
    }
  };

  const approveAllExtracted = async () => {
    if (!activeJob) return;
    const pending = rows.filter(r => r.review_status === 'pending' && (r.extracted_answers || []).length > 0);
    if (pending.length === 0) { toast.info('No pending rows with extracted answers.'); return; }
    if (!confirm(`Approve ${pending.length} rows using the auto-extracted answers?`)) return;
    let ok = 0;
    for (const r of pending) {
      const meta = (r as any).extracted_meta || {};
      const answers = (r.extracted_answers || []).map((a: any) => ({ question_code: a.question_code, value: a.value }));
      try {
        await api.post(`/imports/rows/${r.id}/review`, {
          answers, approve: true,
          site_reference: r.site_reference || meta.site_reference || '',
          client_name: meta.client_name || 'Historical import',
          surveyor_name: meta.surveyor_name || 'Imported (Deposita historical)',
        });
        ok += 1;
      } catch { /* keep going */ }
    }
    toast.success(`Approved ${ok} of ${pending.length} rows`);
    openJob(activeJob.id);
  };

  const approveReview = async () => {
    if (!reviewing) return;
    const answers = Object.entries(reviewAnswers).filter(([, v]) => v !== '').map(([question_code, value]) => ({ question_code, value }));
    try {
      await api.post(`/imports/rows/${reviewing.id}/review`, { answers, approve: true, ...reviewMeta });
      toast.success('Row approved into structured survey result');
      setReviewing(null); openJob(activeJob!.id);
    } catch (err: unknown) { toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed'); }
  };
  const rejectReview = async () => {
    if (!reviewing) return;
    await api.post(`/imports/rows/${reviewing.id}/review`, { answers: [], approve: false, notes: 'Rejected during review' });
    toast.success('Row rejected'); setReviewing(null); openJob(activeJob!.id);
  };

  // -------- OneDrive Inbound scanner ----------
  const [provisioning, setProvisioning] = useState<boolean>(false);
  const [resettingLog, setResettingLog] = useState<boolean>(false);

  const provisionInboundFolder = async (): Promise<void> => {
    setProvisioning(true);
    try {
      const r = await api.post<{ path: string; web_url?: string }>('/imports/onedrive/provision');
      const path = r.data?.path ?? 'RAPTOR/InboundQueue';
      toast.success(
        r.data?.web_url
          ? `Provisioned ${path} in OneDrive`
          : `${path} is ready in OneDrive`,
      );
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Provisioning failed';
      toast.error(msg);
    } finally {
      setProvisioning(false);
    }
  };

  const resetInboundLog = async (scope: 'orphaned' | 'all'): Promise<void> => {
    setResettingLog(true);
    try {
      const r = await api.post<{ deleted: number; scope: string }>(`/imports/onedrive/reset-log?scope=${scope}`);
      const n = r.data?.deleted ?? 0;
      if (n === 0) {
        toast.info('Scanned log is already clean — no entries removed.');
      } else {
        toast.success(
          scope === 'all'
            ? `Cleared ${n} entries from the scanned log — next scan will re-ingest every PDF.`
            : `Removed ${n} orphaned entries — next scan will re-ingest any PDFs whose rows were deleted.`,
        );
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Reset failed';
      toast.error(msg);
    } finally {
      setResettingLog(false);
    }
  };

  const abortScan = async (): Promise<void> => {
    if (!scanId) return;
    if (!confirm('Force-cancel this scan? Only use this if the scan is stuck at 0% or has been running longer than expected.')) return;
    try {
      await api.post(`/imports/scans/${scanId}/abort`);
      toast.success('Scan aborted');
      setScanState({ status: 'failed', error: 'Aborted' });
      if (scanTimerRef.current) { clearInterval(scanTimerRef.current); scanTimerRef.current = null; }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Abort failed';
      toast.error(msg);
    }
  };

  const [scanStarting, setScanStarting] = useState<boolean>(false);

  const startOneDriveScan = async (): Promise<void> => {
    if (scanStarting) return; // Guard against double-click race
    if (scanState?.status === 'queued' || scanState?.status === 'running') {
      toast.info('A scan is already running. Wait for it to finish before starting another one.');
      return;
    }
    setScanStarting(true);
    try {
      const r = await api.post<{ scan_id: string }>('/imports/scan-onedrive');
      setScanId(r.data.scan_id);
      toast.success('OneDrive scan started');
      setScanState({ status: 'queued' });
      setScanEntries([]);
    } catch (err: unknown) {
      const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
      // 409 = concurrent scan guard on the backend. detail may be an object.
      if (response?.status === 409) {
        const d = response.data?.detail;
        const msg = typeof d === 'string'
          ? d
          : (d as { message?: string })?.message ?? 'Another OneDrive scan is already running.';
        const activeId = typeof d === 'object' ? (d as { active_scan_id?: string }).active_scan_id : undefined;
        toast.warning(msg);
        if (activeId) {
          // Attach to the running scan so the user can watch progress instead of starting a new one.
          setScanId(activeId);
          setScanState({ status: 'running' });
        }
      } else {
        const detail = response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : 'Scan failed';
        toast.error(msg);
      }
    } finally {
      setScanStarting(false);
    }
  };

  const refreshScan = async (id: string): Promise<void> => {
    try {
      const [s, e] = await Promise.all([
        api.get<Record<string, unknown>>(`/imports/scans/${id}`),
        api.get<Array<Record<string, unknown>>>(`/imports/scans/${id}/entries?limit=500`),
      ]);
      setScanState(s.data);
      setScanEntries(e.data ?? []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (!scanId) return;
    void refreshScan(scanId);
    // 1s polling while a scan is in-flight so the counters + current
    // file update near-instantly. Cheap: the endpoint is a single
    // Mongo find_one by _id.
    scanTimerRef.current = setInterval(() => void refreshScan(scanId), 1000);
    return () => { if (scanTimerRef.current) clearInterval(scanTimerRef.current); };
  }, [scanId]);

  useEffect(() => {
    if (!scanState) return;
    const status = String(scanState.status ?? '');
    // Detect a terminal-state transition (running → succeeded / failed).
    // Fire the completion dialog exactly once per scan so the user is
    // notified even if they minimised the tab. lastScanStatusRef guards
    // against re-firing every poll tick.
    const prev = lastScanStatusRef.current;
    if ((status === 'succeeded' || status === 'failed') && prev !== status) {
      setScanCompletion({
        status: status as 'succeeded' | 'failed',
        ingested: Number(scanState.ingested ?? 0),
        duplicate: Number(scanState.duplicate ?? 0),
        failed: Number(scanState.failed ?? 0),
        discovered: Number(scanState.discovered ?? 0),
        error: scanState.error ? String(scanState.error) : undefined,
      });
      if (scanTimerRef.current) { clearInterval(scanTimerRef.current); scanTimerRef.current = null; }
    }
    lastScanStatusRef.current = status;
  }, [scanState]);

  // -------- Authenticated photo thumbnail loading ----------
  // The /imports/photos/{id}/thumb endpoint is protected by the imports
  // permission set, so we cannot let the browser fetch it via <img
  // src> (that request would go without the Bearer token → 401 → alt
  // text). Instead, whenever the review modal opens a new set of
  // photos, we fetch each one via the authenticated axios client,
  // convert the byte stream to a blob URL, and hand THAT to <img>. The
  // effect also cleans up the ObjectURLs when the modal closes / row
  // changes, so we don't leak them across the session.
  useEffect(() => {
    // Photos gone — release any URLs we created for the previous set.
    setPhotoUrls((prev) => {
      Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
      return {};
    });
    if (!reviewPhotos.length) return;
    let cancelled = false;
    const createdUrls: string[] = [];
    (async () => {
      const nextUrls: Record<string, string> = {};
      for (const ph of reviewPhotos) {
        try {
          const resp = await api.get(`/imports/photos/${ph.id}/thumb`, {
            responseType: 'blob',
          });
          if (cancelled) break;
          const url = URL.createObjectURL(resp.data as Blob);
          createdUrls.push(url);
          nextUrls[ph.id] = url;
          // Progressive rendering — commit each URL as it arrives so the
          // grid populates one photo at a time instead of waiting for
          // all of them.
          setPhotoUrls((prev) => ({ ...prev, [ph.id]: url }));
        } catch {
          /* leave slot blank — the <img> will render its alt text */
        }
      }
      if (cancelled) {
        createdUrls.forEach((u) => URL.revokeObjectURL(u));
      } else {
        // No-op: nextUrls was committed progressively above.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reviewPhotos]);

  // "OK" handler for the completion dialog — refreshes the jobs list and
  // auto-opens the OneDrive Inbound job for the current day so the user
  // lands directly on the report view without having to hunt for it.
  const acknowledgeScanCompletion = async (): Promise<void> => {
    setScanCompletion(null);
    try {
      const jobsRes = await api.get<Job[]>('/imports/jobs');
      setJobs(jobsRes.data);
      // The auto-managed job label is "OneDrive Inbound — YYYY-MM-DD".
      // Prefer the most recently created onedrive_inbound job so we always
      // open the batch the user just ran (matches the backend logic in
      // `_ensure_inbound_job`).
      const today = new Date().toISOString().slice(0, 10);
      const inbound = jobsRes.data.find(
        (j) => (j as unknown as { source?: string; label?: string }).source === 'onedrive_inbound'
          && String((j as unknown as { label?: string }).label ?? '').includes(today),
      ) ?? jobsRes.data.find((j) => (j as unknown as { source?: string }).source === 'onedrive_inbound');
      if (inbound) {
        await openJob(inbound.id);
        // Best-effort: scroll the job list card into view so the user
        // sees the newly-active job highlighted.
        setTimeout(() => {
          document.querySelector('[data-testid="import-job-detail"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } catch { /* silent — the dialog has closed already */ }
  };

  const filteredScanEntries = scanFilter ? scanEntries.filter((e) => e.status === scanFilter) : scanEntries;

  // ---------- Excel import rollback ----------
  const rollbackExcelJob = async (jobId: string): Promise<void> => {
    if (!window.confirm('Delete this Excel import job and every site/survey it created?  This cannot be undone.')) return;
    setRollingBackId(jobId);
    try {
      await api.delete(`/imports/excel/${jobId}`);
      toast.success('Excel import rolled back');
      const ex = await api.get<ExcelJob[]>('/imports/excel');
      setExcelJobs(ex.data || []);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      // The job doc is already gone (e.g. deleted elsewhere, or a legacy build
      // threw on a missing staged file). Treat as removed and refresh the list
      // so a discarded job can never get stuck on screen.
      if (status === 404) {
        toast.success('Import discarded');
        try {
          const ex = await api.get<ExcelJob[]>('/imports/excel');
          setExcelJobs(ex.data || []);
        } catch { /* ignore */ }
        return;
      }
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      let msg = 'Rollback failed';
      if (typeof detail === 'string') msg = detail;
      else if (detail && typeof detail === 'object' && 'message' in (detail as Record<string, unknown>)) {
        msg = String((detail as Record<string, unknown>).message);
      }
      toast.error(msg);
    } finally { setRollingBackId(null); }
  };

  const excelStatusTone = (s?: string): 'success' | 'blue' | 'warning' | 'muted' => {
    if (s === 'committed') return 'success';
    if (s === 'dry_run_ready' || s === 'configured') return 'blue';
    if (s === 'staged') return 'warning';
    return 'muted';
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1"><h2 className="font-headline text-3xl text-raptor-navy tracking-wide">PDF Imports</h2><p className="text-sm text-raptor-navy/60 font-body">Bulk-ingest historical inspection PDFs. If an upload matches a previous document by file fingerprint or site reference, the older version is automatically replaced so analytics never double-counts a survey. Review the queue, correct anything the extractor missed, then approve.</p></div>
        <div className="flex gap-2 flex-wrap items-center shrink-0">
          <Button
            variant="outline"
            onClick={() => void provisionInboundFolder()}
            disabled={provisioning}
            data-testid="provision-onedrive-btn"
            title="Create RAPTOR/InboundQueue in your OneDrive if it does not already exist"
            className="whitespace-nowrap"
          >
            <FolderPlus size={16} className="mr-1.5 shrink-0" /> {provisioning ? 'Provisioning…' : 'Provision Folders'}
          </Button>
          <Button
            variant="outline"
            onClick={() => void startOneDriveScan()}
            data-testid="scan-onedrive-btn"
            className="whitespace-nowrap"
            disabled={scanStarting || scanState?.status === 'queued' || scanState?.status === 'running'}
          >
            <Cloud size={16} className="mr-1.5 shrink-0" />
            {scanStarting
              ? 'Starting…'
              : (scanState?.status === 'running' || scanState?.status === 'queued')
                ? 'Scanning…'
                : 'Scan OneDrive Inbound'}
          </Button>
          <Button variant="gradient" onClick={() => setShowChooser(true)} className="whitespace-nowrap" data-testid="new-import-btn">
            <Plus size={16} className="mr-1.5 shrink-0" /> New import job
          </Button>
        </div>
      </div>

      {/* --- Import-type chooser modal --- */}
      {showChooser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-raptor-navy/40 backdrop-blur-sm p-4" role="dialog" data-testid="import-chooser-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 space-y-4">
            <div>
              <h3 className="font-headline text-xl text-raptor-navy">Choose import type</h3>
              <p className="text-sm text-raptor-navy/60 font-body">Pick the format of the file you&rsquo;re importing.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => { setShowChooser(false); setCreating(true); }}
                className="text-left p-4 border-2 border-border/60 rounded-md hover:border-raptor-blue hover:bg-raptor-blue/5 transition-colors"
                data-testid="choose-pdf"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-md bg-raptor-blue/10 flex items-center justify-center">
                    <FileText size={20} className="text-raptor-blue" />
                  </div>
                  <div>
                    <div className="font-semibold text-raptor-navy">PDF ingest</div>
                    <div className="text-[11px] text-raptor-navy/60">Legacy survey PDFs</div>
                  </div>
                </div>
                <ul className="text-xs text-raptor-navy/70 space-y-0.5 pl-1 font-body">
                  <li>&bull; One PDF = one survey submission</li>
                  <li>&bull; OCR + manual review of extracted answers</li>
                  <li>&bull; Photos preserved from the PDF</li>
                </ul>
              </button>
              <button
                type="button"
                onClick={() => { setShowChooser(false); nav('/insurer/imports/excel/new'); }}
                className="text-left p-4 border-2 border-border/60 rounded-md hover:border-raptor-blue hover:bg-raptor-blue/5 transition-colors"
                data-testid="choose-excel"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-md bg-emerald-100 flex items-center justify-center">
                    <FileSpreadsheet size={20} className="text-emerald-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-raptor-navy">Excel spreadsheet</div>
                    <div className="text-[11px] text-raptor-navy/60">Structured bulk import</div>
                  </div>
                </div>
                <ul className="text-xs text-raptor-navy/70 space-y-0.5 pl-1 font-body">
                  <li>&bull; Many sites &amp; surveys in one .xlsx</li>
                  <li>&bull; Auto-mapped by column header</li>
                  <li>&bull; No photos (photos not in the spreadsheet)</li>
                </ul>
              </button>
            </div>
            <div className="flex justify-end pt-2">
              <Button variant="ghost" size="sm" onClick={() => setShowChooser(false)} data-testid="chooser-close">Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* --- Excel bulk-import jobs panel (Commit B) --- */}
      {excelJobs.length > 0 && (
        <Card data-testid="excel-jobs-panel">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileSpreadsheet size={16} className="text-emerald-600" /> Excel bulk imports
              <Badge variant="muted" className="ml-1 text-[10px]">{excelJobs.length}</Badge>
            </CardTitle>
            <CardDescription>Recent .xlsx bulk imports. Open one to resume the wizard, or roll back a committed import to remove its sites and surveys (guardrails prevent deletion if Confirmation-of-Cover documents point at the data).</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-auto rounded-b-md">
              <table className="w-full text-sm font-body">
                <thead className="bg-raptor-neutral/40 text-xs text-raptor-navy/70 uppercase">
                  <tr>
                    <th className="text-left py-2 px-3">File</th>
                    <th className="text-left py-2 px-3">Client</th>
                    <th className="text-left py-2 px-3">Template</th>
                    <th className="text-left py-2 px-3">Rows</th>
                    <th className="text-left py-2 px-3">Status</th>
                    <th className="text-left py-2 px-3">Created</th>
                    <th className="text-right py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {excelJobs.map((j) => (
                    <tr key={j.id} className="border-t border-border/60" data-testid={`excel-job-row-${j.id}`}>
                      <td className="py-2 px-3">
                        <div className="font-semibold text-raptor-navy break-all">{j.filename}</div>
                        <div className="text-[11px] text-raptor-navy/50 font-tabular">{j.id}</div>
                      </td>
                      <td className="py-2 px-3 text-raptor-navy">{j.client_name_cache || <span className="italic text-raptor-navy/40">\u2014</span>}</td>
                      <td className="py-2 px-3 text-raptor-navy">{j.template_name_cache ? `${j.template_name_cache} v${j.template_version ?? ''}` : <span className="italic text-raptor-navy/40">\u2014</span>}</td>
                      <td className="py-2 px-3 font-tabular">{j.row_count.toLocaleString()}</td>
                      <td className="py-2 px-3">
                        <Badge variant={excelStatusTone(j.status)} className="text-[10px]">{j.status || 'staged'}</Badge>
                        {j.commit_summary && (
                          <div className="text-[10px] text-raptor-navy/60 mt-0.5 font-tabular">
                            +{j.commit_summary.sites_created} sites &middot; +{j.commit_summary.results_created} surveys
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-[11px] text-raptor-navy/60 font-tabular whitespace-nowrap">{j.created_at.slice(0, 16).replace('T', ' ')}</td>
                      <td className="py-2 px-3 text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => nav(`/insurer/imports/excel/${j.id}`)} data-testid={`open-excel-job-${j.id}`}>Open</Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void rollbackExcelJob(j.id)}
                            disabled={rollingBackId === j.id}
                            title={j.status === 'committed'
                              ? 'Delete this import and every site/survey it created'
                              : 'Discard this staged/configured job'}
                            data-testid={`rollback-excel-job-${j.id}`}
                            className="text-red-700 hover:bg-red-50"
                          >
                            {rollingBackId === j.id ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} className="mr-1" />}
                            {j.status === 'committed' ? 'Roll back' : 'Discard'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}


      {(scanId || scanState) && (
        <Card data-testid="onedrive-scan-panel">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cloud size={18} className="text-raptor-blue" /> OneDrive Inbound Queue scan
              {scanState?.status ? (
                <Badge variant={
                  scanState.status === 'succeeded' ? 'success' :
                  scanState.status === 'failed' ? 'destructive' :
                  scanState.status === 'running' ? 'blue' : 'muted'
                } className="ml-1">
                  {String(scanState.status)}
                </Badge>
              ) : null}
              <button className="ml-auto p-1 hover:bg-raptor-neutral/40 rounded" onClick={() => scanId && void refreshScan(scanId)} title="Refresh">
                <RefreshCw size={14} />
              </button>
              {(scanState?.status === 'running' || scanState?.status === 'queued') && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void abortScan()}
                  data-testid="abort-scan-btn"
                  className="ml-1 h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                >
                  <XCircle size={14} className="mr-1" /> Abort
                </Button>
              )}
            </CardTitle>
            <CardDescription>
              Scans <code className="text-xs">RAPTOR/InboundQueue</code> at the root of your configured OneDrive. The folder is created automatically the first time you scan (or when you click <em>Provision Folders</em>). Each PDF is extracted through the same pipeline as manual uploads. Duplicates (by Graph file id or SHA-256) are skipped; failures are shown below so you can address them.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {scanState && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
                <Stat label="Discovered" value={Number(scanState.discovered ?? 0)} tone="muted" />
                <Stat label="Ingested"   value={Number(scanState.ingested ?? 0)}   tone="success" />
                <Stat label="Duplicates" value={Number(scanState.duplicate ?? 0)}  tone="blue" />
                <Stat label="Failed"     value={Number(scanState.failed ?? 0)}     tone="destructive" />
                <Stat label="Progress"   value={`${Number(scanState.progress_pct ?? 0)}%`} tone="muted" />
              </div>
            )}
            {scanState && (scanState.status === 'running' || scanState.status === 'queued') && scanState.current_file ? (
              <div className="text-xs text-raptor-navy/70 font-body flex items-center gap-2 bg-raptor-blue/5 border border-raptor-blue/20 rounded-md p-2" data-testid="scan-current-file">
                <RefreshCw size={12} className="animate-spin text-raptor-blue shrink-0" />
                <span>
                  <span className="font-semibold text-raptor-navy">{String(scanState.current_step ?? 'processing').replace(/_/g, ' ')}:</span>{' '}
                  <code className="font-tabular text-raptor-navy/90">{String(scanState.current_file)}</code>
                  {scanState.current_index ? <span className="text-raptor-navy/50"> ({Number(scanState.current_index)} / {Number(scanState.discovered ?? 0)})</span> : null}
                </span>
              </div>
            ) : null}
            {scanState?.status === 'succeeded'
              && Number(scanState.ingested ?? 0) === 0
              && Number(scanState.duplicate ?? 0) > 0 && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm flex items-start gap-2"
                data-testid="all-duplicates-warning"
              >
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">Every file was already ingested previously.</div>
                  <div className="text-xs mt-1">
                    RAPTOR remembers every PDF it has already imported (by Graph file id + SHA-256) so it never
                    double-imports the same file. If you deleted the old import job and want these PDFs re-ingested,
                    clear the scanned-file log below.
                  </div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={resettingLog}
                      onClick={async () => {
                        if (!confirm('Remove log entries whose import rows were deleted? This is safe — PDFs whose rows still exist will still be recognised as duplicates.')) return;
                        await resetInboundLog('orphaned');
                      }}
                      data-testid="reset-log-orphaned-btn"
                    >
                      <RotateCcw size={14} className="mr-1.5" />
                      {resettingLog ? 'Working…' : 'Clear orphaned entries'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={resettingLog}
                      onClick={async () => {
                        if (!confirm('Wipe the ENTIRE scanned-file log? The next scan will re-ingest every PDF in RAPTOR/InboundQueue, including files that are already in RAPTOR. Continue?')) return;
                        await resetInboundLog('all');
                      }}
                      data-testid="reset-log-all-btn"
                      className="text-amber-900 hover:bg-amber-100"
                    >
                      Wipe entire log
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {scanState?.error ? (
              <div className="rounded-md border border-red-300 bg-red-50 text-red-800 p-3 text-sm flex items-start gap-2" data-testid="scan-error">
                <AlertTriangle size={16} className="mt-0.5" />
                <div>{String(scanState.error)}</div>
              </div>
            ) : null}
            {scanState?.message ? (
              <div className="text-xs text-raptor-navy/60 font-body">{String(scanState.message)}</div>
            ) : null}
            <div className="flex gap-2 items-center flex-wrap">
              <span className="text-xs text-raptor-navy/60">Filter:</span>
              {(['', 'ingested', 'duplicate', 'failed'] as const).map((f) => (
                <button
                  key={f || 'all'}
                  onClick={() => setScanFilter(f)}
                  data-testid={`scan-filter-${f || 'all'}`}
                  className={`px-2 py-1 rounded text-xs border transition-colors ${
                    scanFilter === f ? 'bg-raptor-blue text-white border-raptor-blue' : 'bg-white border-border/60 hover:bg-raptor-neutral/40'
                  }`}
                >
                  {f || 'All'}
                </button>
              ))}
              <span className="ml-auto text-xs text-raptor-navy/50">{filteredScanEntries.length} entries</span>
            </div>
            <div className="max-h-80 overflow-auto rounded border border-border/60" data-testid="scan-entries">
              <table className="w-full text-xs font-body">
                <thead className="bg-raptor-neutral/40 sticky top-0">
                  <tr>
                    <th className="p-2 text-left">File</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Details</th>
                    <th className="p-2 text-left">Ingested at</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredScanEntries.length === 0 && (
                    <tr><td colSpan={4} className="p-4 text-center text-raptor-navy/50 italic">No entries yet.</td></tr>
                  )}
                  {filteredScanEntries.map((e) => (
                    <tr key={String(e.id)} className="border-t border-border/60" data-testid={`scan-entry-${e.status}`}>
                      <td className="p-2 truncate max-w-[240px]" title={String(e.file_name)}>{String(e.file_name)}</td>
                      <td className="p-2">
                        {e.status === 'ingested'   && <Badge variant="success">Ingested</Badge>}
                        {e.status === 'duplicate' && <Badge variant="blue">Duplicate</Badge>}
                        {e.status === 'failed'    && <Badge variant="destructive">Failed</Badge>}
                      </td>
                      <td className="p-2 text-raptor-navy/70 break-words max-w-[400px]">
                        {String(e.error ?? e.duplicate_of ?? '—')}
                      </td>
                      <td className="p-2 font-tabular text-raptor-navy/60 whitespace-nowrap">{String(e.ingested_at ?? '')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scan completion dialog — fires when a OneDrive scan reaches a
          terminal state (succeeded/failed) so operators get a clear
          summary and one-click path into the review workflow. */}
      {scanCompletion && (
        <div
          className="fixed inset-0 bg-raptor-navy/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setScanCompletion(null)}
          data-testid="scan-completion-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="scan-completion-title"
        >
          <div
            className="bg-white rounded-md max-w-lg w-full shadow-xl animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-border/60 flex items-start gap-2">
              {scanCompletion.status === 'succeeded' ? (
                <CheckCircle2 size={20} className="text-emerald-600 mt-0.5 shrink-0" />
              ) : (
                <AlertTriangle size={20} className="text-amber-600 mt-0.5 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div id="scan-completion-title" className="font-headline text-lg text-raptor-navy leading-tight">
                  {scanCompletion.status === 'succeeded'
                    ? 'OneDrive scan complete'
                    : 'OneDrive scan finished with issues'}
                </div>
                <div className="text-sm text-raptor-navy/60 font-body mt-0.5">
                  {scanCompletion.status === 'succeeded'
                    ? 'Every eligible PDF was processed. Click OK to jump straight to the import job — you can then review and approve each row.'
                    : 'The scan hit a problem. Any PDFs that were processed before the failure are safe and will be picked up by the next scan.'}
                </div>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="rounded-md border border-border/60 bg-raptor-neutral/40 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-raptor-navy/60">Discovered</div>
                  <div className="text-lg font-headline text-raptor-navy">{scanCompletion.discovered}</div>
                </div>
                <div className="rounded-md border border-emerald-300 bg-emerald-50 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-900/70">Ingested</div>
                  <div className="text-lg font-headline text-emerald-900">{scanCompletion.ingested}</div>
                </div>
                <div className="rounded-md border border-raptor-blue/40 bg-raptor-blue/10 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-raptor-navy/70">Duplicates</div>
                  <div className="text-lg font-headline text-raptor-navy">{scanCompletion.duplicate}</div>
                </div>
                <div className={`rounded-md border p-2 ${
                  scanCompletion.failed > 0 ? 'border-red-300 bg-red-50' : 'border-border/60 bg-raptor-neutral/40'
                }`}>
                  <div className="text-[10px] uppercase tracking-wide text-raptor-navy/60">Failed</div>
                  <div className={`text-lg font-headline ${scanCompletion.failed > 0 ? 'text-red-800' : 'text-raptor-navy'}`}>
                    {scanCompletion.failed}
                  </div>
                </div>
              </div>
              {scanCompletion.error ? (
                <div className="rounded-md border border-red-300 bg-red-50 text-red-800 p-2 text-xs font-body">
                  {scanCompletion.error}
                </div>
              ) : null}
              {scanCompletion.failed > 0 && scanCompletion.status === 'succeeded' ? (
                <div className="text-xs text-raptor-navy/60 font-body">
                  Tip: re-run the scan to retry the failed file(s). Only the failures will be re-attempted;
                  everything already ingested will be skipped as a duplicate.
                </div>
              ) : null}
            </div>
            <div className="p-4 border-t border-border/60 flex justify-end gap-2">
              <Button
                variant="gradient"
                onClick={() => void acknowledgeScanCompletion()}
                data-testid="scan-completion-ok-btn"
                autoFocus
              >
                OK — open the report
              </Button>
            </div>
          </div>
        </div>
      )}

      {creating && (
        <Card>
          <CardHeader><CardTitle>New import job</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={createJob} className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Template</Label><Select required value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })}>{templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</Select></div>
              <div className="md:col-span-2 space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="md:col-span-2 flex justify-end"><Button type="submit" variant="gradient">Create job</Button></div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-[300px_1fr] gap-4">
        <Card>
          <CardHeader><CardTitle className="text-sm">Jobs · {jobs.length}</CardTitle></CardHeader>
          <CardContent className="p-2 space-y-1">
            {jobs.length === 0 && <div className="text-sm text-raptor-navy/50 py-4 text-center">No jobs yet.</div>}
            {jobs.map(j => (
              <div key={j.id} className={`w-full text-left px-3 py-2 rounded-md text-sm font-body flex items-start gap-2 ${activeJob?.id === j.id ? 'bg-raptor-blue/15 font-semibold' : 'hover:bg-raptor-neutral/50'}`}>
                <button onClick={() => openJob(j.id)} className="flex-1 min-w-0 text-left">
                  <div className="font-tabular text-xs text-raptor-navy/50">{j.id}</div>
                  <div>{j.name}</div>
                  <div className="text-xs text-raptor-navy/60">{j.total_rows} PDFs · {j.approved_rows} approved · {j.rejected_rows} rejected</div>
                </button>
                <button data-testid={`delete-job-${j.id}`} onClick={async () => {
                  if (!confirm(`Delete job "${j.name}"? This also removes ${j.total_rows} PDFs, ${j.approved_rows} approved results, and any synthetic instructions. Cannot be undone.`)) return;
                  try { await api.delete(`/imports/jobs/${j.id}`); toast.success('Job deleted'); if (activeJob?.id === j.id) { setActiveJob(null); setRows([]); } await load(); }
                  catch (err: unknown) { toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Delete failed'); }
                }} className="p-1 hover:bg-red-100 rounded" title="Delete job">
                  <Trash size={14} className="text-red-500" />
                </button>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          {activeJob ? (
            <>
              <CardHeader className="flex-row items-center justify-between">
                <div><CardTitle>{activeJob.name}</CardTitle><CardDescription>Template v{activeJob.template_version} · {activeJob.total_rows} PDFs · {activeJob.approved_rows}/{activeJob.reviewed_rows} approved</CardDescription></div>
                <div className="flex items-center gap-2">
                  <Button data-testid="approve-all-extracted-btn" variant="outline" size="sm" onClick={approveAllExtracted} disabled={rows.filter(r => r.review_status === 'pending' && (r.extracted_answers || []).length > 0).length === 0}>
                    <CheckCircle2 size={14} /> Approve all extracted
                  </Button>
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center gap-1 px-3 h-9 rounded-md bg-raptor-gradient text-white text-sm"><Upload size={14} /> Upload PDFs</span>
                    <input ref={fileRef} type="file" accept=".pdf" multiple className="hidden" onChange={(e) => uploadPdfs(e.target.files)} />
                  </label>
                </div>
              </CardHeader>
              <CardContent className="p-0" data-testid="import-job-detail">
                <table className="w-full text-sm font-body">
                  <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70"><tr><th className="py-2 px-3 text-left">Row ID</th><th className="py-2 px-3 text-left">Site ref</th><th className="py-2 px-3 text-left">Source PDF</th><th className="py-2 px-3 text-left">Status</th><th className="py-2 px-3"></th></tr></thead>
                  <tbody>
                    {rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-raptor-navy/50">No PDFs uploaded yet.</td></tr>}
                    {rows.map(r => {
                      const replacedCount = (r.replaces || []).length;
                      return (
                      <tr key={r.id} className="border-t border-border/60">
                        <td className="py-2 px-3 font-tabular text-xs">{r.id}</td>
                        <td className="py-2 px-3 font-tabular">{r.site_reference || '—'}</td>
                        <td className="py-2 px-3 text-xs text-raptor-navy/60">
                          {(r.original_filename || r.source_key.split('/').pop() || '').toString()}
                        </td>
                        <td className="py-2 px-3">
                          {r.ocr_status === 'extracted' && <Badge variant="success" className="mr-1">{(r.extracted_answers || []).length} extracted</Badge>}
                          {replacedCount > 0 && (
                            <Badge
                              variant="blue"
                              className="mr-1"
                              title={`This upload replaced ${replacedCount} previous version${replacedCount === 1 ? '' : 's'}: ${(r.replaces || []).join(', ')}`}
                              data-testid={`row-replaced-${r.id}`}
                            >
                              Replaced older
                            </Badge>
                          )}
                          {r.review_status === 'approved' ? <Badge variant="success">Approved</Badge> : r.review_status === 'rejected' ? <Badge variant="destructive">Rejected</Badge> : <Badge variant="muted">Pending</Badge>}
                        </td>
                        <td className="py-2 px-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openReview(r)}
                            disabled={r.review_status === 'superseded'}
                            data-testid={`review-btn-${r.id}`}
                          >
                            {r.review_status === 'approved'
                              ? 'View'
                              : r.review_status === 'rejected'
                                ? 'View'
                                : 'Review'}
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </>
          ) : <CardContent className="py-8 text-center text-raptor-navy/60">Select a job or create a new one.</CardContent>}
        </Card>
      </div>

      {reviewing && reviewSchema && (        <div className="fixed inset-0 bg-raptor-navy/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setReviewing(null)}>
          <div className="bg-white rounded-md max-w-4xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-border/60 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <div className="font-headline text-xl text-raptor-navy">
                  {reviewReadOnly ? 'Approved survey' : 'Review row'} {reviewing.id}
                </div>
                {reviewReadOnly && (
                  <Badge variant={reviewing.review_status === 'approved' ? 'success' : 'destructive'} data-testid="review-readonly-badge">
                    {reviewing.review_status === 'approved' ? 'Approved · read-only' : 'Rejected · read-only'}
                  </Badge>
                )}
              </div>
              <div className="text-sm text-raptor-navy/60 font-body">
                {reviewReadOnly
                  ? <>This row was already reviewed — the answers below are the version that was saved to the survey store. To make edits, ask an admin to un-approve the row (feature coming soon).</>
                  : (reviewing.extracted_answers || []).length > 0
                    ? <><b className="text-emerald-700">{reviewing.extracted_answers.length} answers pre-filled from PDF text extraction.</b> Site ref, client &amp; surveyor were auto-detected where possible. Verify each section, correct anything the parser missed, then approve.</>
                    : <>Enter values from the Deposita PDF. On approve, this becomes a structured survey result linked to a synthetic instruction.</>}
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid md:grid-cols-3 gap-3">
                <Input placeholder="Site reference (red number)" value={reviewMeta.site_reference} onChange={(e) => setReviewMeta({ ...reviewMeta, site_reference: e.target.value })} disabled={reviewReadOnly} />
                <Input placeholder="Client name" value={reviewMeta.client_name} onChange={(e) => setReviewMeta({ ...reviewMeta, client_name: e.target.value })} disabled={reviewReadOnly} />
                <Input placeholder="Surveyor name" value={reviewMeta.surveyor_name} onChange={(e) => setReviewMeta({ ...reviewMeta, surveyor_name: e.target.value })} disabled={reviewReadOnly} />
              </div>
              {reviewSchema.sections.map((sec: any) => (
                <div key={sec.section_code} className="border border-border/60 rounded-md p-3">
                  <div className="font-headline text-raptor-navy mb-2">{sec.number}. {sec.title}</div>
                  <div className="grid md:grid-cols-2 gap-2">
                    {sec.questions.map((q: any) => (
                      <div key={q.question_code} className="grid grid-cols-[80px_1fr] gap-2 items-start">
                        <div className="text-xs font-tabular text-raptor-blue pt-2">{q.display_number}</div>
                        <div>
                          <div className="text-xs text-raptor-navy/70 font-body">{q.text}</div>
                          {(q.type === 'yes_no' || q.type === 'yes_no_na') ? (
                            <Select value={reviewAnswers[q.question_code] ?? ''} onChange={(e) => setReviewAnswers({ ...reviewAnswers, [q.question_code]: e.target.value })} className="h-8 text-xs" disabled={reviewReadOnly}>
                              <option value="">—</option><option value="yes">Yes</option><option value="no">No</option>{q.type === 'yes_no_na' && <option value="na">N/A</option>}
                            </Select>
                          ) : q.type === 'select' ? (
                            <Select value={reviewAnswers[q.question_code] ?? ''} onChange={(e) => setReviewAnswers({ ...reviewAnswers, [q.question_code]: e.target.value })} className="h-8 text-xs" disabled={reviewReadOnly}>
                              <option value="">—</option>{(q.options ?? []).map((o: string) => <option key={o} value={o}>{o}</option>)}
                            </Select>
                          ) : (
                            <Input value={reviewAnswers[q.question_code] ?? ''} onChange={(e) => setReviewAnswers({ ...reviewAnswers, [q.question_code]: e.target.value })} className="h-8 text-xs" disabled={reviewReadOnly} />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {reviewReadOnly && reviewPhotos.length > 0 && (
                <div className="border border-border/60 rounded-md p-3" data-testid="review-photo-grid">
                  <div className="font-headline text-raptor-navy mb-2">Site photos ({reviewPhotos.length})</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {reviewPhotos.map((ph) => {
                      const url = photoUrls[ph.id];
                      return (
                        <div
                          key={ph.id}
                          className="rounded-md border border-border/40 bg-raptor-neutral/30 overflow-hidden aspect-[3/4] relative"
                          data-testid={`review-photo-${ph.id}`}
                        >
                          {url ? (
                            <img
                              src={url}
                              alt={`Site photo ${ph.id} (page ${ph.page ?? '?'})`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-xs text-raptor-navy/40 font-body animate-pulse">
                              Loading…
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-border/60 sticky bottom-0 bg-white flex justify-end gap-2">
              <Button variant="outline" onClick={() => setReviewing(null)}>Close</Button>
              {!reviewReadOnly && (
                <>
                  <Button variant="destructive" onClick={rejectReview}><XCircle size={14} /> Reject</Button>
                  <Button variant="gradient" onClick={approveReview}><CheckCircle2 size={14} /> Approve</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
